import { availabilityApi, zimbaApi, contentApi } from '@/infrastructure/Configuration'
import { DateFactory } from 'rhg-vue-common'
import { multiOffersService } from '@/infrastructure/Factory'
import cloneDeep from 'lodash/cloneDeep'
import flattenDeep from 'lodash/flattenDeep'
import { getQueryParams, reflect } from '@/infrastructure/Utils'

export default class MultiOffersService {
  constructor(httpClient) {
    this.httpClient = httpClient
  }

  async getMultiOffer(detailPageUrl) {
    const params = {
      detailPageUrl,
      geotargetCountryCode: global.AkamaiService.userCountry()
    }
    const url = `${zimbaApi}/query/offers`
    const { data } = await this.httpClient.get(url, { params })

    return data
  }

  getFiltersFromUrl(offer) {
    const { countryCode, cityCode, checkInDate, checkOutDate } = getQueryParams(
      window.location.search
    )
    const filterToApply = {
      code: countryCode || cityCode || '',
      type: getFilterType({ countries: countryCode, cities: cityCode })
    }
    const { startDate, endDate } = multiOffersService.calculateDefaultDates(
      offer,
      checkInDate,
      checkOutDate
    )

    const datesFromUrl = {
      startDate,
      endDate
    }

    return { filterToApply, datesFromUrl }
  }

  async getInitialMultiOffer() {
    const detailPageUrl = getDetailPageUrl()
    const data = await this.getMultiOffer(detailPageUrl)

    const allDestinations = parseAllDestinations(data)
    const clone = cloneDeep(allDestinations)

    const hotels = parseMapHotels(data)

    // sort countries alphabetically
    clone.sort((a, b) => a.name.localeCompare(b.name))

    // sort cities alphabetically
    clone.forEach(
      country => (country.cities = country.cities.sort((a, b) => a.name.localeCompare(b.name)))
    )

    return {
      offer: data.offers[0],
      allDestinations: clone,
      hotels
    }
  }

  getHotDestinations(hotCities, allDestinations) {
    const allCities = allDestinations.reduce(
      (accumulator, curValue) => accumulator.concat(curValue.cities),
      []
    )
    const hotDestinations = hotCities.map(city => {
      const cities = allCities.find(c => c.code === city.code)
      return ({
        ...cities,
        image: city.image,
        name: city.title
      })
    })
    return hotDestinations
  }

  async getMultiPriceFrom(hotelCodes, startDate, endDate, pacCode, corporateId) {
    let searchType = ''
    let promotionCode = ''
    if (corporateId) {
      searchType = '&searchType=corporateAccount'
      promotionCode = `&promotionCode=${corporateId}`
    } else if (pacCode) {
      searchType = '&searchType=pac'
      promotionCode = `&promotionCode=${pacCode}`
    } else {
      searchType = '&searchType=lowest'
    }

    const countryCookie =
      !!window.Cookie.read(window.cookieDictionary.akamai) &&
      window.Utils.parseQueryString(window.Cookie.read(window.cookieDictionary.akamai)).country

    let taxIncludedRegion = false
    try {
      taxIncludedRegion = window.ConfigDynamic.valueOf(
        window?.configDynamicKey.DEFAULT_TAXES_INCLUDED_COUNTRY_CODES
      )
        .split(',')
        .some(country => country === countryCookie)
    } catch {
      taxIncludedRegion = false
    }

    const url = `${availabilityApi}/multiple-price-from?hotelCodes=${hotelCodes}&startDate=${multiOffersService.formatDate(
      startDate
    )}&endDate=${multiOffersService.formatDate(
      endDate
    )}${searchType}${promotionCode}&futureDays=62&origin=${
      window.configDynamicKey.PRICE_OFFERS_MULTI.origin
    }&sourceOfBusiness=${
      window.device.desktop() ? 'desktop' : 'mobile'
    }&taxIncludedRegion=${taxIncludedRegion}&adults=2&children=0`
    const { data } = await this.httpClient.get(url, {
      headers: {
        'x-currency': window.Cookie.read(window.cookieDictionary.preferredCurrency)
      }
    })

    return data
  }

  getAllHotels(allDestinations) {
    const allCities = allDestinations.reduce(
      (accumulator, curValue) => accumulator.concat(curValue.cities),
      []
    )

    return allCities.reduce((accumulator, curValue) => accumulator.concat(curValue.hotels), [])
  }

  async getPrices(allHotels, startDate, endDate, pacCode, corporateId) {
    const parsedHotelCodes = parseHotelCodes(allHotels)
    const result = await Promise.all(
      parsedHotelCodes
        .map(async hotelCodes =>
          multiOffersService.getMultiPriceFrom(hotelCodes, startDate, endDate, pacCode, corporateId)
        )
        .map(reflect)
    )
    return result.map((response, index) => {
      if (response.status === 'rejected') {
        return {
          hotels: parsedHotelCodes[index].split(',').map(hotelCode => {
            const hotel = allHotels.find(hotel => hotel.code === hotelCode)
            return {
              ...hotel,
              priceFrom: {
                amount: 0
              }
            }
          })
        }
      } else {
        return response.v
      }
    })
  }

  addPricesToHotels(allDestinations, prices) {
    const clone = cloneDeep(allDestinations)
    const allPrices = flattenDeep(prices.map(chunk => chunk.hotels))

    allPrices.forEach(hotel =>
      clone.map(country => setPriceFrom(country, hotel.code, hotel?.priceFrom))
    )
    return clone
  }

  addPricesToCities(allDestinations) {
    const clone = cloneDeep(allDestinations)

    clone.forEach(country =>
      country.cities.forEach(city => {
        const filteredHotelsAmount = city.hotels.filter(hotel => hotel?.priceFrom?.amount !== 0)
        city.priceFrom =
          filteredHotelsAmount.length > 0
            ? filteredHotelsAmount[0]?.priceFrom
            : city.hotels[0]?.priceFrom
      })
    )
    return clone
  }

  formatDate(date) {
    return DateFactory.formatDate(date, 'YYYY-MM-DD')
  }

  async getCityByCode(code) {
    const url = `${zimbaApi}/find/city/${code}`

    try {
      const { data } = await this.httpClient.get(url)
      return data
    } catch {
      return { name: code }
    }
  }

  async getCountryByCode(code) {
    const url = `${contentApi}/countries`

    const {
      data: { countries }
    } = await this.httpClient.get(url)
    const country = countries.find(c => c.code === code)

    return country || { name: code }
  }

  calculateDefaultDates(offer, checkInDate, checkOutDate) {
    const reservationStartDate = DateFactory.formatDate(offer.reservationStartDate)
    const actualDate = DateFactory.formatDate(DateFactory.now())

    const checkOut = DateFactory.formatDate(offer.reservationEndDate)
    const minimumDays = offer.minimumNights || 1
    const reservationLastDate = DateFactory.addDays(checkOut, 1)
    const started = actualDate > reservationStartDate

    let result
    if (started) {
      if (
        checkInDate &&
        checkOutDate &&
        checkInDate >= DateFactory.formatDate(actualDate) &&
        checkInDate < DateFactory.formatDate(reservationLastDate) &&
        checkOutDate <= DateFactory.formatDate(reservationLastDate) &&
        DateFactory.daysBetween(checkInDate, checkOutDate) >= minimumDays
      ) {
        result = {
          startDate: checkInDate,
          endDate: checkOutDate
        }
      } else {
        result = {
          startDate: actualDate,
          endDate: DateFactory.addDays(actualDate, minimumDays)
        }
      }
    }
    if (!started) {
      if (
        checkInDate &&
        checkOutDate &&
        checkInDate >= DateFactory.formatDate(reservationStartDate) &&
        checkInDate < DateFactory.formatDate(reservationLastDate) &&
        checkOutDate <= DateFactory.formatDate(reservationLastDate) &&
        DateFactory.daysBetween(checkInDate, checkOutDate) >= minimumDays
      ) {
        result = {
          startDate: checkInDate,
          endDate: checkOutDate
        }
      } else {
        result = {
          startDate: reservationStartDate,
          endDate: DateFactory.addDays(reservationStartDate, minimumDays)
        }
      }
    }
    return {
      startDate: DateFactory.formatDate(result.startDate),
      endDate: DateFactory.formatDate(result.endDate)
    }
  }

  multiOffersRedirect(params) {
    const hasLandingType = window.location.search.includes('landingType')
    const landingTypeParameter = 'landingType=multioffers'
    let finalUrl = ''

    const url = Object.keys(params).reduce((acc, currentKey) => {
      const currentKeyValue = params[currentKey]
      if (!currentKeyValue) {
        return acc
      }

      return !acc ? '?' + currentKey + '=' + currentKeyValue : acc + '&' + currentKey + '=' + currentKeyValue
    }, '')

    if (hasLandingType) {
      finalUrl = `${url}&${landingTypeParameter}`
    } else {
      finalUrl = window.location.search.includes(url) ? window.location.search : url + window.location.search.replace('?', '&')
    }
    return finalUrl
  }
}

function setPriceFrom(obj, code, priceFrom) {
  for (const prop in obj) {
    if (typeof obj[prop] === 'object') {
      setPriceFrom(obj[prop], code, priceFrom)
    } else {
      if (obj[prop] === code && prop === 'code') {
        obj.priceFrom = priceFrom
      }
    }
  }
  return obj
}

const parseHotelCodes = allHotels => {
  const hotelsPerChunk = 20
  const chunkedHotels = allHotels.reduce((acc, curr, index) => {
    const chunk = Math.floor(index / hotelsPerChunk)
    acc[chunk] = [].concat(acc[chunk] || [], curr)
    return acc
  }, [])
  return chunkedHotels.map(chunkHotel =>
    chunkHotel.reduce((acc, curr) => `${acc}${!acc ? '' : ','}${curr.code}`, '')
  )
}

const getDetailPageUrl = () => {
  if (window.location.origin.includes('localhost')) {
    return '/en-us/hotel-deals/gourmet-experience'
  }
  return window.location.pathname
}

const parseAllDestinations = offersFilter => {
  return Object.keys(offersFilter.countries).map(country => ({
    code: country,
    name: offersFilter.countries[country].name,
    cities: filterCitiesbyCountry(offersFilter.cities, country, offersFilter.hotels),
    image: offersFilter.countries[country].image
  }))
}

const parseMapHotels = data => {
  return Object.values(data.hotels).map(hotel => {
    return {
      ...hotel,
      city: (data.cities[hotel.cityCode] || {}).name || '',
      country: (data.countries[hotel.countryCode] || {}).name || ''
    }
  })
}

const filterHotelsbyCity = (hotels, city) => {
  return Object.keys(hotels)
    .filter(hotel => hotels[hotel].cityCode === city)
    .map(c => ({
      code: c,
      name: hotels[c].name,
      brand: hotels[c].brand,
      overviewPath: hotels[c].overviewPath
    }))
}

const filterCitiesbyCountry = (cities, country, hotels) =>
  Object.keys(cities)
    .filter(city => cities[city].countryOrStateCode === country)
    .map(c => ({
      code: c,
      name: cities[c].name,
      hotels: filterHotelsbyCity(hotels, c),
      image: cities[c].image
    }))

const getFilterType = filter => {
  if (filter.countries) {
    return 'country'
  } else {
    return 'city'
  }
}
